import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";

import { PositionsDataContext } from "../../../context/PositionsDataContext";
import { deleteSecurity, fetchSecurityInfo } from "../../../services/Axios";

export default function SecurityMaster() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { datasets } = useContext(PositionsDataContext);

  // -------------------- State --------------------
  const [securityInfo, setSecurityInfo] = useState([]);
  const [globalFilter, setGlobalFilter] = useState(
    () => localStorage.getItem("securityMasterFilter") || ""
  );

  // -------------------- Effects --------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchSecurityInfo();
        setSecurityInfo(data);
      } catch (error) {
        console.error("Error fetching security info:", error);
      }
    };

    fetchData();
  }, []);

  // The below effect is commented out as this will be ad-hoc call when the page is loaded.
  // useEffect(() => {
  //   if (datasets?.securitiesData) {
  //     setSecurityInfo(datasets.securitiesData);
  //   }
  // }, [datasets]);

  useEffect(() => {
    localStorage.setItem("securityMasterFilter", globalFilter);
  }, [globalFilter]);

  // -------------------- Handlers --------------------
  const handleEdit = (row) => {
    navigate(`/Edit_Security/${row.original.id}`);
  };

  const handleDelete = async (row) => {
    if (window.confirm("Are you sure you want to delete this security?")) {
      try {
        await deleteSecurity(row.original.id);
        const updatedData = await fetchSecurityInfo();
        setSecurityInfo(updatedData);
      } catch (error) {
        console.error("Error deleting security:", error);
      }
    }
  };

  // -------------------- Table Columns --------------------
  const columns = useMemo(
    () => [
      { accessorKey: "SYMBOL", header: "Symbol" },
      { accessorKey: "name", header: "Name" },
      {
        accessorKey: "last_closing_price",
        header: "Prior Day Closing Price",
        size: 200,
        muiTableHeadCellProps: { align: "right" },
        muiTableBodyCellProps: { align: "right" },
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value
            ? new Intl.NumberFormat("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 4,
              }).format(value)
            : "-";
        },
      },
      { accessorKey: "security_type", header: "Security Type" },
      { accessorKey: "security_type2", header: "Security Type 2" },
      { accessorKey: "currency", header: "Currency" },
      { accessorKey: "pricing_source", header: "Pricing Source" },
    ],
    []
  );

  // -------------------- Filtered Data --------------------
  const filteredData = useMemo(() => {
    if (!globalFilter) return securityInfo;
    return securityInfo.filter((item) =>
      Object.values(item).some((val) =>
        val?.toString().toLowerCase().includes(globalFilter.toLowerCase())
      )
    );
  }, [securityInfo, globalFilter]);

  // -------------------- Table Config --------------------
  const table = useMaterialReactTable({
    columns,
    data: filteredData,
    enableColumnOrdering: true,
    enableColumnResizing: true,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableSorting: true,
    enableTopToolbar: true,
    enablePagination: true,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
      density: "compact",
    },
    enableRowActions: true,
    positionActionsColumn: "first",
    displayColumnDefOptions: {
      "mrt-row-actions": {
        size: 100,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
    },
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", gap: "0.3rem", justifyContent: "center" }}>
        <IconButton
          color="primary"
          onClick={() => handleEdit(row)}
          size="small"
          sx={{ p: "2px" }}
        >
          <EditIcon />
        </IconButton>
        <IconButton
          color="secondary"
          onClick={() => navigate(`/Copy_Security/${row.original.id}`)}
          size="small"
          sx={{ p: "2px" }}
        >
          <CopyIcon />
        </IconButton>
        <IconButton
          onClick={() => handleDelete(row)}
          size="small"
          sx={{
            color: "#d32f2f",
            p: "1px",
            "&:hover": { color: "#b71c1c" },
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    ),
    muiTableBodyRowProps: {
      sx: { height: "30px" },
    },
  });

  // -------------------- Render --------------------
  return (
    <Box
      sx={{
        height: "calc(100vh - 130px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: "8px 20px",
          backgroundColor: "white",
          borderBottom: "1px solid #e0e0e0",
          zIndex: 2,
        }}
      >
        <Typography fontSize="1.5rem" fontWeight="bold" mb={1}>
          Security Master
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="contained"
            onClick={() => navigate("/Create_Security")}
            sx={{
              backgroundColor: "rgb(0, 104, 66)",
              "&:hover": { backgroundColor: "rgb(0, 84, 46)" },
            }}
          >
            Create Security
          </Button>
          <TextField
            placeholder="Search..."
            variant="outlined"
            size="small"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            sx={{ width: "300px" }}
          />
        </Box>
      </Box>

      {/* Table Body */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          backgroundColor: "#f5f5f5",
          p: 1,
        }}
      >
        <MaterialReactTable
          table={table}
          muiTableContainerProps={{
            sx: {
              maxHeight: "calc(100vh - 200px)",
              overflow: "auto",
            },
          }}
        />
      </Box>
    </Box>
  );
}
